import { Table } from "@mantine/core"
import { ReactNode } from "react"
import type { ColumnType } from "../table"
import classes from "./fit-table.module.css"

type Props<T> = {
  columns: ColumnType<T>[]
  rows: T[]
  rowKey?: (row: T, index: number) => string
  /** Shown in one cell across the table when there are no rows. */
  empty?: ReactNode
}

/**
 * Line table for the supplier order detail pages (Ord_SalesDetail / Ord_PurchaseDetail):
 * fixed layout, so the columns without a `width` share what is left and long text wraps
 * inside its cell instead of pushing a horizontal scrollbar under the card.
 */
export function FitTable<T>({ columns, rows, rowKey, empty }: Props<T>) {
  return (
    <Table className={classes.table} layout="fixed" withRowBorders={false} verticalSpacing={0} horizontalSpacing={0}>
      <Table.Thead>
        <Table.Tr>
          {columns.map((column, i) => (
            <Table.Th
              key={i}
              className={classes.th}
              w={column.width}
              ta={column.titleAlign ?? column.align ?? "left"}
            >
              {column.renderCheck && rows.length > 0 ? column.renderCheck({ rows } as T, -1) : column.title}
            </Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.length === 0 && empty ? (
          <Table.Tr>
            <Table.Td colSpan={columns.length} className={classes.empty}>{empty}</Table.Td>
          </Table.Tr>
        ) : (
          rows.map((row, r) => (
            <Table.Tr key={rowKey ? rowKey(row, r) : r} className={classes.tr}>
              {columns.map((column, c) => (
                <Table.Td key={c} className={classes.td} ta={column.align ?? "left"}>
                  {column.render(row, r)}
                </Table.Td>
              ))}
            </Table.Tr>
          ))
        )}
      </Table.Tbody>
    </Table>
  )
}
